import React, { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Modal,
  Form,
  Select,
  DatePicker,
  Input,
  InputNumber,
  Button,
  Space,
  Table,
  Tag,
  Divider,
  Typography,
  Row,
  Col,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { createQuotationRequest } from '../../redux/actions/quotationActions';

const { Option } = Select;
const { Text } = Typography;

const TAX_SLABS = ['0', '5', '12', '18', '28'];

const fmtMoney = (v) =>
  v != null ? `₹ ${Number(v).toFixed(2)}` : '—';

const calcItem = (qty, rate, disc, taxSlab) => {
  const q = parseFloat(qty) || 0;
  const r = parseFloat(rate) || 0;
  const d = parseFloat(disc) || 0;
  const t = parseFloat(taxSlab) || 0;
  const subtotal      = q * r;
  const discountAmt   = subtotal * d / 100;
  const taxableAmt    = subtotal - discountAmt;
  const taxAmt        = taxableAmt * t / 100;
  const amount        = taxableAmt + taxAmt;
  return { subtotal, discountAmt, taxableAmt, taxAmt, amount };
};

const EMPTY_ITEM = () => ({
  key: Date.now(),
  product_id: undefined,
  product_name: '',
  unit: '',
  hsn_sac_code: '',
  quantity: 1,
  quoted_rate: 0,
  tax_slab: '18',
  discount: 0,
});

/**
 * QuotationForm
 *
 * Props:
 *  open    — boolean
 *  onClose — callback
 */
const QuotationForm = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const { vendors } = useSelector((s) => s.vendors || {});
  const { products } = useSelector((s) => s.products || {});
  const { actionLoading } = useSelector((s) => s.quotations);

  const [form] = Form.useForm();
  const [items, setItems] = useState([EMPTY_ITEM()]);

  const updateItem = (key, field, value) => {
    setItems((prev) =>
      prev.map((item) => (item.key === key ? { ...item, [field]: value } : item))
    );
  };

  const addItem = () => setItems((prev) => [...prev, EMPTY_ITEM()]);

  const removeItem = (key) => {
    if (items.length === 1) return;
    setItems((prev) => prev.filter((i) => i.key !== key));
  };

  const handleProductSelect = (productId, itemKey) => {
    const product = (products || []).find((p) => p.id === productId);
    if (product) {
      setItems((prev) =>
        prev.map((i) =>
          i.key === itemKey
            ? {
                ...i,
                product_id: productId,
                product_name: product.product_name || product.name || '',
                unit: product.unit || '',
                hsn_sac_code: product.hsn_sac_code || '',
                quoted_rate: product.purchase_rate || 0,
                tax_slab: product.tax_slab ? String(product.tax_slab) : '18',
              }
            : i
        )
      );
    }
  };

  // Computed totals
  const totals = useMemo(() => {
    return items.reduce(
      (acc, item) => {
        const c = calcItem(item.quantity, item.quoted_rate, item.discount, item.tax_slab);
        acc.amount      += c.amount;
        acc.taxAmt      += c.taxAmt;
        acc.discountAmt += c.discountAmt;
        return acc;
      },
      { amount: 0, taxAmt: 0, discountAmt: 0 }
    );
  }, [items]);

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      const validItems = items.filter((i) => i.product_id && i.quantity > 0 && i.quoted_rate > 0);
      if (validItems.length === 0) {
        return;
      }

      dispatch(
        createQuotationRequest({
          vendor_id:      values.vendor_id,
          quotation_date: values.quotation_date.format('YYYY-MM-DD'),
          valid_until:    values.valid_until ? values.valid_until.format('YYYY-MM-DD') : undefined,
          remarks:        values.remarks || '',
          items: validItems.map((i) => ({
            product_id:   i.product_id,
            product_name: i.product_name,
            unit:         i.unit,
            hsn_sac_code: i.hsn_sac_code,
            quantity:     parseFloat(i.quantity),
            quoted_rate:  parseFloat(i.quoted_rate),
            tax_slab:     i.tax_slab,
            discount:     parseFloat(i.discount) || 0,
          })),
        })
      );
      handleClose();
    });
  };

  const handleClose = () => {
    form.resetFields();
    setItems([EMPTY_ITEM()]);
    onClose();
  };

  const itemColumns = [
    {
      title: 'Product',
      key: 'product',
      width: 200,
      render: (_, record) => (
        <Select
          showSearch
          style={{ width: '100%' }}
          placeholder="Select product"
          value={record.product_id}
          onChange={(val) => handleProductSelect(val, record.key)}
          filterOption={(input, option) =>
            option?.children?.toLowerCase().includes(input.toLowerCase())
          }
          size="small"
        >
          {(products || []).map((p) => (
            <Option key={p.id} value={p.id}>
              {p.product_name || p.name}
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: 'Unit',
      key: 'unit',
      width: 70,
      render: (_, record) => (
        <Input
          size="small"
          value={record.unit}
          onChange={(e) => updateItem(record.key, 'unit', e.target.value)}
          placeholder="unit"
        />
      ),
    },
    {
      title: 'Qty',
      key: 'qty',
      width: 80,
      render: (_, record) => (
        <InputNumber
          size="small"
          style={{ width: '100%' }}
          min={0.01}
          precision={2}
          value={record.quantity}
          onChange={(val) => updateItem(record.key, 'quantity', val)}
        />
      ),
    },
    {
      title: 'Rate (₹)',
      key: 'rate',
      width: 100,
      render: (_, record) => (
        <InputNumber
          size="small"
          style={{ width: '100%' }}
          min={0}
          precision={2}
          value={record.quoted_rate}
          onChange={(val) => updateItem(record.key, 'quoted_rate', val)}
        />
      ),
    },
    {
      title: 'Disc %',
      key: 'discount',
      width: 80,
      render: (_, record) => (
        <InputNumber
          size="small"
          style={{ width: '100%' }}
          min={0}
          max={100}
          precision={2}
          value={record.discount}
          onChange={(val) => updateItem(record.key, 'discount', val)}
        />
      ),
    },
    {
      title: 'GST %',
      key: 'tax_slab',
      width: 90,
      render: (_, record) => (
        <Select
          size="small"
          style={{ width: '100%' }}
          value={record.tax_slab}
          onChange={(val) => updateItem(record.key, 'tax_slab', val)}
        >
          {TAX_SLABS.map((s) => (
            <Option key={s} value={s}>{s}%</Option>
          ))}
        </Select>
      ),
    },
    {
      title: 'Amount',
      key: 'amount',
      width: 100,
      align: 'right',
      render: (_, record) => {
        const c = calcItem(record.quantity, record.quoted_rate, record.discount, record.tax_slab);
        return (
          <Text strong style={{ color: '#1890ff', fontSize: 12 }}>
            {fmtMoney(c.amount)}
          </Text>
        );
      },
    },
    {
      title: '',
      key: 'remove',
      width: 36,
      render: (_, record) => (
        <Tooltip title="Remove item">
          <Button
            type="text"
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => removeItem(record.key)}
            disabled={items.length === 1}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <Modal
      title={
        <Space>
          <SaveOutlined style={{ color: '#1890ff' }} />
          Create Quotation
        </Space>
      }
      open={open}
      onCancel={handleClose}
      footer={
        <Space>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={actionLoading}
            onClick={handleSubmit}
          >
            Save Quotation
          </Button>
        </Space>
      }
      width={920}
      destroyOnClose
      styles={{ body: { maxHeight: '75vh', overflowY: 'auto' } }}
    >
      <Form form={form} layout="vertical" initialValues={{ tax_slab: '18' }}>
        <Row gutter={16}>
          <Col xs={24} sm={8}>
            <Form.Item
              name="vendor_id"
              label="Vendor"
              rules={[{ required: true, message: 'Vendor is required' }]}
            >
              <Select
                showSearch
                placeholder="Select vendor"
                filterOption={(input, option) =>
                  option?.children?.toLowerCase().includes(input.toLowerCase())
                }
              >
                {(vendors || []).map((v) => (
                  <Option key={v.id} value={v.id}>{v.firm_name}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item
              name="quotation_date"
              label="Quotation Date"
              rules={[{ required: true, message: 'Date is required' }]}
              initialValue={dayjs()}
            >
              <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item name="valid_until" label="Valid Until (optional)">
              <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="remarks" label="Remarks">
          <Input.TextArea rows={2} placeholder="Notes about this quotation…" maxLength={500} showCount />
        </Form.Item>
      </Form>

      <Divider orientation="left" style={{ fontSize: 13 }}>
        Items
        <Button
          size="small"
          type="dashed"
          icon={<PlusOutlined />}
          onClick={addItem}
          style={{ marginLeft: 12 }}
        >
          Add Item
        </Button>
      </Divider>

      <Table
        columns={itemColumns}
        dataSource={items}
        rowKey="key"
        pagination={false}
        size="small"
        scroll={{ x: 740 }}
      />

      {/* Totals footer */}
      <div
        style={{
          marginTop: 12,
          padding: '10px 16px',
          background: '#f0f5ff',
          borderRadius: 6,
          border: '1px solid #adc6ff',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 32,
        }}
      >
        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>Discount</Text>
          <div style={{ color: '#52c41a', fontWeight: 600 }}>−{fmtMoney(totals.discountAmt)}</div>
        </div>
        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>Tax / GST</Text>
          <div style={{ color: '#fa8c16', fontWeight: 600 }}>{fmtMoney(totals.taxAmt)}</div>
        </div>
        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>Grand Total</Text>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#0050b3' }}>{fmtMoney(totals.amount)}</div>
        </div>
      </div>
    </Modal>
  );
};

export default QuotationForm;
